import { useState, useMemo, useCallback } from 'react';
import { Stack, PrimaryButton, MessageBar, MessageBarType, Link, SpinnerSize, Spinner, Label, Persona, PersonaSize } from '@fluentui/react';
import { ROUTES } from '../../../config/routes';
import { HealAPI } from '../../../healApi';
import { Visit } from '../../../redux/visits/types';
import { useCart } from '../CartContext';
import { usePatientVisits } from '../PatientVisitsContext';
import { PatientInsuranceCards } from '../../patient/insurances/PatientInsuranceCards';
import { VisitSummary } from '../../../api';
import { PatientVisits } from '../../patient/PatientVisits';

export function ReviewAndBook() {
  const { state: cart, dispatch } = useCart();
  const [bookedVisit, setBookedVisit] = useState<Visit>();
  const [error, setError] = useState('');
  const [patientVisits] = usePatientVisits();

  const createVisit = useCallback(() => {
    const request = {
      userAccountId: cart.who.accountId,
      patientId: cart.who.patientId,
      addressId: cart.where.addressId,
      serviceCode: cart.what.serviceCode,
      subServiceCode: cart.what.subServiceCode,
      symptoms: cart.what.symptoms,
      timeSlotId: cart.when.timeSlotId,
      selectedDoctorId: cart.when.pcp ? cart.who.patient?.preferredDoctor?.doctor?.providerId ?? '' : '',
      bookingSource: 'operations_web', 
    };

    dispatch({ type: 'set_creating', data: true });
    HealAPI.saveVisit(request).then((r) => {
      if (r.ok) {
        setBookedVisit(r.data.visit);
      } else {
        setError(r.data.description);
      }
      dispatch({ type: 'set_creating', data: false });
    });
  }, [dispatch, cart.who, cart.where, cart.what, cart.when]);

  const canCreateVisit = useMemo(() => {
    return !cart.creating && !!cart.who.accountId && !!cart.who.patientId && !!cart.where.addressId && !!cart.what.serviceCode && !!cart.when.timeSlotId;
  }, [cart]);

  const bookAnother = useCallback(() => {
    dispatch({ type: 'reset' });
  }, [dispatch]);

  const patient = cart.who.patient;

  return (
    <Stack tokens={{ childrenGap: 12 }}>
      {error && !bookedVisit && <MessageBar messageBarType={MessageBarType.error}>{error}</MessageBar>}
      {cart.creating && <Spinner style={{ marginTop: 20 }} size={SpinnerSize.large} label="Creating visit..." />}
      {!cart.creating && bookedVisit && (
        <>
          <span>
            Visit <Link href={`${ROUTES.visits}/${bookedVisit.visitCode}`}>{bookedVisit.visitCode}</Link> has been successfully created.
          </span>
          <br />
          <PrimaryButton style={{ width: '100%', height: 50 }} text="Create Another Visit" onClick={bookAnother} />
        </>
      )}
      {!cart.creating && !bookedVisit && (
        <>
          <Persona
            size={PersonaSize.size72}
            text={`${patient.fullName}`}
            secondaryText={`${patient.dateOfBirth} | ${patient.gender} | ${patient.phone}`}
            tertiaryText={patient.email}
            imageUrl={patient.avatarUrl}
          />
          <div>
            <MessageBar messageBarType={MessageBarType.warning}>Please review and validate the insurance before creating the visit.</MessageBar>
            <Label className="ms-font-xl">Patient Insurances</Label>
            <PatientInsuranceCards patientId={cart.who.patientId} />
          </div>
          <br />
          {patientVisits.length > 0 && <DuplicateVisitWarning patient={cart.who.patient} visits={patientVisits} />}
          <PrimaryButton style={{ width: '100%', height: 50 }} onClick={() => createVisit()} disabled={!canCreateVisit} text="Create Visit" data-tid="btn_next" />
        </>
      )}
    </Stack>
  );
}

function DuplicateVisitWarning({ patient, visits }: { patient: any; visits: VisitSummary[] }) {
  return (
    <div>
      <MessageBar messageBarType={MessageBarType.warning}>
        <p>
          Warning: <b>{patient.firstName}</b> has the following future appointment(s) scheduled. Please ensure this is not a duplicate booking before proceeding.
        </p>
      </MessageBar>

      <PatientVisits visits={visits} title="Upcoming Visits" />
    </div>
  );
}
